import { FontAwesomeSvgIcon } from 'components/shared/index';
import {
  Box,
  createFilterOptions,
  CreateFilterOptionsConfig,
  FilterOptionsState
} from '@mui/material';
import { HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete, {
  AutocompleteRenderInputParams
} from '@mui/material/Autocomplete';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { faStar as faStarLight } from '@fortawesome/pro-light-svg-icons';
import { mutations } from 'RestAPIs/rapini';
import { UserFavoriteReferenceType } from 'generated/index';
import { useTranslation } from 'react-i18next';
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteValue
} from '@mui/base/AutocompleteUnstyled/useAutocomplete';

interface FavoriteAutocompleteProps<T> {
  disabled?: boolean;
  disableSorting?: boolean;
  disableFavoriteFilter?: boolean;
  disableClearable?: boolean;
  options: T[];
  value?: AutocompleteValue<T, false, false, false>;
  entityIdProperty?: string;
  idProperty?: string;
  optionLabelProp?: string;
  isFavoriteProp?: string;
  sortByProp?: string;
  fullWidth?: boolean;
  type: UserFavoriteReferenceType;
  renderInput: (params: AutocompleteRenderInputParams) => React.ReactNode;
  onChange?: (
    event: React.SyntheticEvent,
    value: AutocompleteValue<T, false, false, false>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>
  ) => void;
}

interface FavoriteOptionProps<T> {
  props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>;
  option: T | ShowMoreOption;
  optionLabelProp: string;
  isFavoriteProp: string;
  changeFavorite: (option: T) => void;
}

interface ShowMoreOption {
  id: string;
  name: string;
}

export default function FavoriteAutocomplete<T>({
  options,
  entityIdProperty = 'entityId',
  idProperty = 'id',
  optionLabelProp = 'name',
  isFavoriteProp = 'isFavorite',
  sortByProp,
  disableSorting,
  disableFavoriteFilter,
  disableClearable,
  disabled = false,
  value,
  type,
  renderInput,
  onChange
}: FavoriteAutocompleteProps<T>) {
  const { t } = useTranslation('common');
  const { mutate: createFavorite } = mutations.useCreateFavorite();
  const { mutate: deleteFavorite } = mutations.useDeleteFavorite();
  const [filterOnFavorite, setFilterOnFavorite] = useState(
    !disableFavoriteFilter
  );
  const [selectedValue, setSelectedValue] = useState<
    AutocompleteValue<T, false, false, false> | ShowMoreOption | undefined
  >(value);
  const [inputValue, setInputValue] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const isActivating = useRef(false);
  const sortBy = sortByProp ?? optionLabelProp;

  const showMoreOption: ShowMoreOption = {
    id: 'showMore',
    name: t('showMore')
  };

  function isShowMoreOption<T>(option: ShowMoreOption | T | undefined) {
    return option?.['id'] === showMoreOption.id;
  }

  function onOptionChange(
    event: React.SyntheticEvent,
    value: AutocompleteValue<T | ShowMoreOption, false, false, false>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T | ShowMoreOption>
  ) {
    if (isShowMoreOption(value)) {
      setFilterOnFavorite(false);
      setSelectedValue(null);
      isActivating.current = true;
    } else {
      setSelectedValue(value);
      onChange?.(
        event,
        value as T,
        reason,
        details as AutocompleteChangeDetails<T>
      );
    }
  }

  function onInputChange(event: React.SyntheticEvent, value: string) {
    if (value !== showMoreOption.name) {
      setInputValue(value);
    }
  }

  function changeFavorite(option: T) {
    if (option[isFavoriteProp]) {
      deleteFavorite({
        referenceEntityId: option[entityIdProperty],
        referenceType: type
      });
    } else {
      createFavorite({
        referenceEntityId: option[entityIdProperty],
        referenceType: type
      });
    }
    option[isFavoriteProp] = !option[isFavoriteProp];
  }

  function FavoriteOption<T>({
    props,
    option,
    optionLabelProp,
    isFavoriteProp,
    changeFavorite
  }: FavoriteOptionProps<T>) {
    const [isHover, setIsHover] = useState(false);
    return (
      <li
        {...props}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <Box sx={{ display: 'flex', width: '100%' }}>
          {isShowMoreOption(option) && (
            <Box sx={{ fontStyle: 'italic' }}>{option['name']}</Box>
          )}
          {!isShowMoreOption(option) && (
            <Box position="relative" width="100%">
              <Box
                sx={{
                  flexGrow: 1,
                  marginRight: '30px',
                  wordBreak: 'break-word',
                  hyphens: 'auto'
                }}
              >
                {option[optionLabelProp]}
              </Box>
              <Box
                sx={{
                  width: '20px',
                  position: 'absolute',
                  right: '-10px',
                  top: '0px'
                }}
              >
                {(option[isFavoriteProp] || isHover) && (
                  <FontAwesomeSvgIcon
                    icon={option[isFavoriteProp] ? faStar : faStarLight}
                    fontSize="inherit"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      changeFavorite(option as T);
                    }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </li>
    );
  }

  const renderOption = (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
    option: T | ShowMoreOption
  ) => {
    return (
      <FavoriteOption
        props={props}
        option={option}
        optionLabelProp={optionLabelProp}
        isFavoriteProp={isFavoriteProp}
        changeFavorite={changeFavorite}
      />
    );
  };

  function preFilterOptions(options: T[]) {
    if (
      filterOnFavorite &&
      options.some((option) => option[isFavoriteProp]) &&
      options.some((option) => !option[isFavoriteProp])
    ) {
      return [
        ...options.filter((option) => option[isFavoriteProp]),
        showMoreOption
      ];
    }
    return options;
  }

  function sortOptions(
    options: (T | ShowMoreOption)[],
    disableSorting?: boolean
  ) {
    if (disableSorting) {
      return options.sort(
        (a, b) => Number(b[isFavoriteProp]) - Number(a[isFavoriteProp])
      );
    }
    return options.sort(
      (a, b) =>
        Number(b[isFavoriteProp]) - Number(a[isFavoriteProp]) ||
        a[sortBy]?.localeCompare(b[sortBy])
    );
  }

  const sortedOptions = useMemo(
    () => sortOptions(preFilterOptions(options), disableSorting),
    [options, disableSorting, filterOnFavorite]
  );

  const filterOptions = (
    options: (T | ShowMoreOption)[],
    state: FilterOptionsState<T | ShowMoreOption>
  ) => {
    const config = {} as CreateFilterOptionsConfig<T | ShowMoreOption>;
    return [
      ...createFilterOptions(config).call(
        undefined,
        options.filter((option) => !isShowMoreOption(option)),
        state
      ),
      ...options.filter((o) => isShowMoreOption(o))
    ];
  };

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  return (
    <Autocomplete
      disabled={disabled}
      open={isOpen}
      renderInput={renderInput}
      options={sortedOptions}
      disableClearable={disableClearable}
      getOptionLabel={(option) => option[optionLabelProp] || option['name']}
      isOptionEqualToValue={(a, b) => a[idProperty] === b[idProperty]}
      value={selectedValue}
      inputValue={inputValue}
      filterOptions={filterOptions}
      noOptionsText={t('noOptions')}
      renderOption={renderOption}
      onChange={onOptionChange}
      onOpen={() => setIsOpen(true)}
      onInputChange={onInputChange}
      onClose={() => {
        setIsOpen(isActivating.current);
        isActivating.current = false;
      }}
    />
  );
}
